/**
 * PATH: apps/qa/src/components/camera-capture/useGeolocation.ts
 * PURPOSE: Hook for GPS tracking with accuracy indicator
 */

import { useState, useEffect, useRef } from 'react'
import type { GeolocationState } from './types'

export function useGeolocation() {
  const [state, setState] = useState<GeolocationState>({
    status: 'unknown',
    latitude: null,
    longitude: null,
    accuracy: null,
  })
  const watchId = useRef<number | null>(null)

  useEffect(() => {
    if (!navigator.geolocation) {
      setState(s => ({ ...s, status: 'unavailable' }))
      return
    }

    setState(s => ({ ...s, status: 'requesting' }))

    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        setState({
          status: 'granted',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        })
      },
      (error) => {
        if (import.meta.env.DEV) console.warn('Geolocation error:', error)
        setState(s => ({
          ...s,
          status: error.code === 1 ? 'denied' : 'unavailable',
        }))
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    )

    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current)
      }
    }
  }, [])

  return state
}
